import type { Pane } from 'tweakpane'

type PresetsItem = {
  id: string
  data: Record<string, string | number>
}

type PresetResponse = {
  status: number
  filenames: string[]
  presets: PresetsItem[]
}

/* --- add new values here --- */
const DEFAULT_PARAMS = {
  lenisLerp: 0.4,
  lenisWheelMultiplier: 1.5,
  scroll: 0,
  scrollProgress: 0,
  scrollDirection: 0,
  isTransitioning: false,

  duration: 2,

  revealStart: 90,
  revealEnd: 70,
  // showMarkers: true,
}

export default defineNuxtPlugin(async () => {
  const config = useRuntimeConfig()

  let fpsGraph
  let pane: Pane | null = null

  const { presets } = await $fetch<PresetResponse>('/api/tweakpane/presets')
  const _localState = import.meta.browser ? localStorage.getItem('TWEAKPANE_STATE') : null

  const PARAMS: typeof DEFAULT_PARAMS = reactive({
    ...DEFAULT_PARAMS,
    ...(presets?.[0]?.data || {}),
    // ...JSON.parse(localState || '{}'),
  })

  // if (import.meta.dev && import.meta.client) {
  //   console.log('Tweakpane loaded:', PARAMS)
  // }

  if (config.public.TWEAKPANE_ENABLED !== 'true') {
    return {
      provide: {
        tweakpane: {
          fpsGraph,
          pane,
          PARAMS,
        },
      },
    }
  }

  const { Pane } = await import('tweakpane')
  const EssentialsPlugin = await import('@tweakpane/plugin-essentials')
  const CamerakitPlugin = await import('@tweakpane/plugin-camerakit')

  const animationState = useAnimationState()

  const loadedPreset = ref('local')

  pane = import.meta.browser ? new Pane({ title: 'Parameters' }) : null

  if (pane) {
    pane.registerPlugin(EssentialsPlugin)
    pane.registerPlugin(CamerakitPlugin)

    fpsGraph = pane.addBlade({
      view: 'fpsgraph',
      label: 'fpsgraph',
    })

    pane.addBinding(PARAMS, 'scroll', {
      readonly: true,
      interval: 16.6 * 3,
    })

    pane.addBinding(PARAMS, 'scrollProgress', {
      readonly: true,
      interval: 16.6 * 3,
    })

    pane.addBinding(PARAMS, 'scrollDirection', {
      readonly: true,
      interval: 16.6 * 3,
    })

    pane.addBinding(PARAMS, 'isTransitioning', {
      readonly: true,
      interval: 16.6 * 3,
    })

    /* --- add new panes here --- */
    const lenisPane = pane.addFolder({
      title: 'Lenis (refresh page on change)',
      expanded: true,
    })

    lenisPane.addBinding(PARAMS, 'lenisLerp', {
      label: 'lerp',
      view: 'cameraring',
      wide: true,
      unit: {
        pixels: 40,
        ticks: 10,
        value: 0.1,
      },
      min: 0,
      max: 1,
      step: 0.01,
    })
    lenisPane.addBinding(PARAMS, 'lenisWheelMultiplier', {
      label: 'wheelMultiplier',
      view: 'cameraring',
      wide: true,
      series: 2,
      unit: {
        pixels: 50,
        ticks: 10,
        value: 1,
      },
      min: 0.1,
      max: 5,
      step: 0.1,
    })

    pane.addBlade({ view: 'separator' })

    pane.addBinding(PARAMS, 'duration')
    pane.addBinding(PARAMS, 'revealStart', {
      label: 'ScrollTrigger start',
      view: 'cameraring',
      wide: true,
      series: 2,

      min: 0,
      max: 100,
      step: 10,
    })
    pane.addBinding(PARAMS, 'revealEnd', {
      label: 'ScrollTrigger end',
      view: 'cameraring',
      wide: true,
      series: 2,

      min: 0,
      max: 100,
      step: 10,
    })
    // pane.addBinding(PARAMS, 'showMarkers', {
    //   label: 'ScrollTrigger markers',
    //   showMarkers: true
    // })

    /* --- end --- */

    const f2 = pane.addFolder({ title: 'Presets', expanded: false })

    /**
     * populate dropdown list with saved presets
     */
    f2.addBlade({
      view: 'list',
      label: 'preset',
      options: [
        ...(presets || []).map((preset) => ({
          text: preset.id,
          value: preset.id,
        })),
        { text: 'local', value: 'local' },
      ],
      value: presets?.[0]?.id || 'local',
    })

    const btn = f2.addButton({ title: 'Save preset' })

    btn.on('click', async () => {
      if (import.meta.dev) {
        try {
          await $fetch('/api/tweakpane/presets', {
            method: 'post',
            body: {
              preset: JSON.stringify(PARAMS),
            },
          })

          window.location.reload()
        } catch (error) {
          console.error(error)
        }
      } else {
        console.log(JSON.stringify(PARAMS))
        window.alert('Current state logged to console')
      }
    })

    pane.on('change', (e) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { label } = e.target as any

      /**
       * if currently selected preset is "local",
       * save the current state in localStorage
       */
      if (loadedPreset.value === 'local' && label !== 'scroll') {
        localStorage.setItem('TWEAKPANE_STATE', JSON.stringify(PARAMS))
      }

      if (label === 'preset') {
        loadedPreset.value = e.value as string

        const localState = localStorage.getItem('TWEAKPANE_STATE')

        const newState =
          e.value === 'local'
            ? JSON.parse(localState || '')
            : presets.find((preset) => preset.id === e.value)?.data

        for (const key in newState) {
          // @ts-expect-error: we know that the key is a string
          PARAMS[key] = newState[key]
        }

        pane.refresh()
      }

      /**
       * reload all saved animations
       */
      if (label !== 'scroll') {
        animationState.refreshAll()
      }
    })
  }

  return {
    provide: {
      tweakpane: {
        fpsGraph,
        pane,
        PARAMS,
      },
    },
  }
})
